<script lang="ts">
  import { onMount } from 'svelte';
  import { goto } from '$app/navigation';
  import { page } from '$app/stores';
  import { _ } from 'svelte-i18n';
  import type { PageData } from './$types';
  import ContentCard from '$lib/components/content/ContentCard.svelte';
  import AdvancedSearchBox from '$lib/components/content/AdvancedSearchBox.svelte';
  
  interface Props {
    data: PageData;
  }
  
  let { data }: Props = $props();
  
  let searchQuery = $state('');
  let selectedCategory = $state(data.filters.category);
  let selectedTag = $state(data.filters.tag);
  
  onMount(() => {
    // Track page view
    if (typeof window !== 'undefined' && window.plausible) {
      window.plausible('Blog Page View', {
        props: { 
          category: selectedCategory || 'all',
          tag: selectedTag || 'none',
          page: data.pagination.currentPage,
          locale: data.locale
        }
      });
    }
  });
  
  function handleCategoryFilter(category: string) {
    selectedCategory = category === selectedCategory ? '' : category;
    updateFilters();
  }
  
  function handleTagFilter(tag: string) {
    selectedTag = tag === selectedTag ? '' : tag;
    updateFilters();
  }
  
  function updateFilters() {
    const params = new URLSearchParams();
    if (selectedCategory) params.set('category', selectedCategory);
    if (selectedTag) params.set('tag', selectedTag);
    
    const url = `/blog${params.toString() ? '?' + params.toString() : ''}`;
    goto(url);
  }
  
  function handlePageChange(newPage: number) {
    const params = new URLSearchParams();
    if (selectedCategory) params.set('category', selectedCategory);
    if (selectedTag) params.set('tag', selectedTag);
    if (newPage > 1) params.set('page', newPage.toString());
    
    const url = `/blog${params.toString() ? '?' + params.toString() : ''}`;
    goto(url);
  }
  
  function clearFilters() {
    selectedCategory = '';
    selectedTag = '';
    goto('/blog');
  }
</script>

<svelte:head>
  <title>{data.meta.title} | Heyweek</title>
  <meta name="description" content={data.meta.description} />
  <link rel="canonical" href="https://heyweek.com{data.meta.canonical}" />
  
  <!-- RSS Feed -->
  <link rel="alternate" type="application/rss+xml" title="Heyweek Blog RSS" href="/rss/blog.xml" />
  
  <!-- Open Graph -->
  <meta property="og:title" content="{data.meta.title} | Heyweek" />
  <meta property="og:description" content={data.meta.description} />
  <meta property="og:type" content="website" />
  <meta property="og:url" content="https://heyweek.com{data.meta.canonical}" />
  
  <!-- Twitter Card -->
  <meta name="twitter:title" content="{data.meta.title} | Heyweek" />
  <meta name="twitter:description" content={data.meta.description} />
</svelte:head>

<div class="blog-page">
  <!-- Hero Section -->
  <section class="blog-hero">
    <div class="container">
      <div class="blog-hero__content">
        <h1 class="blog-hero__title">
          {#if selectedCategory}
            {selectedCategory} {$_('blog.posts')}
          {:else if selectedTag}
            {$_('blog.taggedWith')} "{selectedTag}"
          {:else}
            {$_('blog.title')}
          {/if}
        </h1>
        
        <p class="blog-hero__subtitle">
          {#if selectedCategory || selectedTag}
            {$_('blog.filtered.subtitle', { values: { count: data.pagination.totalPosts } })}
          {:else}
            {$_('blog.subtitle')}
          {/if}
        </p>
        
        <!-- Search -->
        <div class="blog-hero__search">
          <AdvancedSearchBox
            bind:query={searchQuery}
            contentIndex={{ blog: data.posts, changelog: [], press: [], stories: [] }}
            locale={data.locale}
            placeholder={$_('blog.search.placeholder')}
          />
        </div>
      </div>
    </div>
  </section>
  
  <!-- Featured Posts -->
  {#if !selectedCategory && !selectedTag && data.pagination.currentPage === 1 && data.featuredPosts.length > 0}
    <section class="featured-posts">
      <div class="container">
        <h2 class="featured-posts__title">{$_('blog.featured.title')}</h2>
        <div class="featured-posts__grid">
          {#each data.featuredPosts as post}
            <ContentCard item={post} locale={data.locale} />
          {/each}
        </div>
      </div>
    </section>
  {/if}
  
  <!-- Filters and Content -->
  <section class="blog-content">
    <div class="container">
      <div class="blog-content__layout">
        <!-- Sidebar Filters -->
        <aside class="blog-sidebar">
          <div class="filter-section">
            <h3 class="filter-section__title">{$_('blog.filters.categories')}</h3>
            <div class="filter-section__options">
              {#each data.categories as category}
                <button
                  class="filter-option {selectedCategory === category ? 'filter-option--active' : ''}"
                  onclick={() => handleCategoryFilter(category)}
                >
                  {category}
                </button>
              {/each}
            </div>
          </div>
          
          <div class="filter-section">
            <h3 class="filter-section__title">{$_('blog.filters.tags')}</h3>
            <div class="filter-section__options filter-section__options--tags">
              {#each data.tags.slice(0, 20) as tag}
                <button
                  class="filter-tag {selectedTag === tag ? 'filter-tag--active' : ''}"
                  onclick={() => handleTagFilter(tag)}
                >
                  #{tag}
                </button>
              {/each}
            </div>
          </div>
          
          {#if selectedCategory || selectedTag}
            <button class="clear-filters" onclick={clearFilters}>
              {$_('blog.filters.clear')}
            </button>
          {/if}
        </aside>
        
        <!-- Main Content -->
        <main class="blog-main">
          {#if data.posts.length > 0}
            <div class="blog-posts">
              <div class="blog-posts__header">
                <h2 class="blog-posts__title">
                  {#if selectedCategory || selectedTag}
                    {$_('blog.filtered.results', { values: { count: data.pagination.totalPosts } })}
                  {:else}
                    {$_('blog.recent.title')}
                  {/if}
                </h2>
                <p class="blog-posts__count">
                  {$_('blog.showing', { 
                    values: { 
                      start: (data.pagination.currentPage - 1) * 12 + 1,
                      end: Math.min(data.pagination.currentPage * 12, data.pagination.totalPosts),
                      total: data.pagination.totalPosts
                    }
                  })}
                </p>
              </div>
              
              <div class="blog-posts__grid">
                {#each data.posts as post}
                  <ContentCard item={post} locale={data.locale} />
                {/each}
              </div>
              
              <!-- Pagination -->
              {#if data.pagination.totalPages > 1}
                <div class="pagination">
                  <button
                    class="pagination__button"
                    disabled={!data.pagination.hasPrevPage}
                    onclick={() => handlePageChange(data.pagination.currentPage - 1)}
                  >
                    ← {$_('pagination.previous')}
                  </button>
                  
                  <div class="pagination__info">
                    {$_('pagination.pageOf', { 
                      values: { 
                        current: data.pagination.currentPage, 
                        total: data.pagination.totalPages 
                      }
                    })}
                  </div>
                  
                  <button
                    class="pagination__button"
                    disabled={!data.pagination.hasNextPage}
                    onclick={() => handlePageChange(data.pagination.currentPage + 1)}
                  >
                    {$_('pagination.next')} →
                  </button>
                </div>
              {/if}

              <!-- Crawlable archive: real <a> links to every post so the oldest
                   articles (behind JS pagination) aren't orphaned. -->
              {#if !selectedCategory && !selectedTag && data.allPosts && data.allPosts.length > 0}
                <nav class="blog-archive" aria-label="All articles">
                  <h2 class="blog-archive__title">All articles</h2>
                  <ul class="blog-archive__list">
                    {#each data.allPosts as post}
                      <li>
                        <a class="blog-archive__link" href="/blog/{post.slug}">{post.title}</a>
                      </li>
                    {/each}
                  </ul>
                </nav>
              {/if}
            </div>
          {:else}
            <div class="blog-empty">
              <div class="blog-empty__icon">📝</div>
              <h3 class="blog-empty__title">{$_('blog.empty.title')}</h3>
              <p class="blog-empty__description">{$_('blog.empty.description')}</p>
              {#if selectedCategory || selectedTag}
                <button class="blog-empty__action" onclick={clearFilters}>
                  {$_('blog.empty.clearFilters')}
                </button>
              {/if}
            </div>
          {/if}
        </main>
      </div>
    </div>
  </section>
</div>

<style>
  .blog-page {
    min-height: 100vh;
  }
  
  .blog-hero {
    background: linear-gradient(135deg, var(--bg-primary) 0%, var(--bg-secondary) 100%);
    padding: var(--space-12) 0 var(--space-8);
    border-bottom: 1px solid var(--border-light);
  }
  
  .blog-hero__content {
    text-align: center;
    max-width: 800px;
    margin: 0 auto;
  }
  
  .blog-hero__title {
    font-size: var(--font-size-3xl);
    font-weight: var(--font-weight-bold);
    color: var(--text-primary);
    margin-bottom: var(--space-4);
    line-height: 1.2;
  }
  
  .blog-hero__subtitle {
    font-size: var(--font-size-lg);
    color: var(--text-secondary);
    margin-bottom: var(--space-8);
    line-height: 1.5;
  }
  
  .featured-posts {
    padding: var(--space-12) 0;
    background: var(--bg-secondary);
  }
  
  .featured-posts__title {
    font-size: var(--font-size-xl);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
    margin-bottom: var(--space-6);
    text-align: center;
  }
  
  .featured-posts__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: var(--space-6);
  }
  
  .blog-content {
    padding: var(--space-8) 0;
  }
  
  .blog-content__layout {
    display: grid;
    grid-template-columns: 250px 1fr;
    gap: var(--space-8);
    align-items: start;
  }
  
  .blog-sidebar {
    position: sticky;
    top: var(--space-8);
  }
  
  .filter-section {
    background: var(--bg-secondary);
    border-radius: var(--radius-lg);
    padding: var(--space-6);
    margin-bottom: var(--space-6);
  }
  
  .filter-section__title {
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
    margin-bottom: var(--space-4);
  }
  
  .filter-section__options {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  
  .filter-section__options--tags {
    flex-direction: row;
    flex-wrap: wrap;
  }
  
  .filter-option {
    padding: var(--space-2) var(--space-3);
    background: none;
    border: 1px solid var(--border-light);
    border-radius: var(--radius-md);
    color: var(--text-primary);
    font-size: var(--font-size-sm);
    cursor: pointer;
    transition: var(--transition-all);
    text-align: left;
  }
  
  .filter-option:hover,
  .filter-option--active {
    background: var(--brand-primary);
    color: white;
    border-color: var(--brand-primary);
  }
  
  .filter-tag {
    padding: var(--space-1) var(--space-3);
    background: none;
    border: 1px solid var(--border-light);
    border-radius: var(--radius-full);
    color: var(--text-secondary);
    font-size: var(--font-size-xs);
    cursor: pointer;
    transition: var(--transition-all);
  }
  
  .filter-tag:hover,
  .filter-tag--active {
    background: var(--brand-primary);
    color: white;
    border-color: var(--brand-primary);
  }
  
  .clear-filters {
    width: 100%;
    padding: var(--space-3);
    background: var(--bg-tertiary);
    border: 1px solid var(--border-light);
    border-radius: var(--radius-md);
    color: var(--text-primary);
    font-size: var(--font-size-sm);
    cursor: pointer;
    transition: var(--transition-colors);
  }
  
  .clear-filters:hover {
    background: var(--bg-primary);
  }
  
  .blog-posts__header {
    margin-bottom: var(--space-6);
  }
  
  .blog-posts__title {
    font-size: var(--font-size-xl);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
    margin-bottom: var(--space-2);
  }
  
  .blog-posts__count {
    font-size: var(--font-size-sm);
    color: var(--text-secondary);
  }
  
  .blog-posts__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: var(--space-6);
    margin-bottom: var(--space-8);
  }
  
  .pagination {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--space-6) 0;
    border-top: 1px solid var(--border-light);
  }
  
  .pagination__button {
    padding: var(--space-3) var(--space-6);
    background: var(--bg-primary);
    border: 1px solid var(--border-light);
    border-radius: var(--radius-md);
    color: var(--text-primary);
    font-size: var(--font-size-sm);
    cursor: pointer;
    transition: var(--transition-all);
  }
  
  .pagination__button:hover:not(:disabled) {
    background: var(--brand-primary);
    color: white;
    border-color: var(--brand-primary);
  }
  
  .pagination__button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  .pagination__info {
    font-size: var(--font-size-sm);
    color: var(--text-secondary);
  }
  
  .blog-empty {
    text-align: center;
    padding: var(--space-12) var(--space-4);
  }
  
  .blog-empty__icon {
    font-size: 4rem;
    margin-bottom: var(--space-4);
    opacity: 0.6;
  }
  
  .blog-empty__title {
    font-size: var(--font-size-xl);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
    margin-bottom: var(--space-3);
  }
  
  .blog-empty__description {
    font-size: var(--font-size-base);
    color: var(--text-secondary);
    line-height: 1.5;
    margin-bottom: var(--space-6);
  }
  
  .blog-empty__action {
    padding: var(--space-3) var(--space-6);
    background: var(--brand-primary);
    color: white;
    border: none;
    border-radius: var(--radius-md);
    font-size: var(--font-size-sm);
    cursor: pointer;
    transition: var(--transition-colors);
  }
  
  .blog-empty__action:hover {
    background: var(--brand-primary-dark);
  }
  
  @media (max-width: 1024px) {
    .blog-content__layout {
      grid-template-columns: 1fr;
      gap: var(--space-6);
    }
    
    .blog-sidebar {
      position: static;
      order: -1;
    }
    
    .filter-section {
      padding: var(--space-4);
    }
  }
  
  @media (max-width: 768px) {
    .blog-hero__title {
      font-size: var(--font-size-xl);
    }
    
    .blog-hero__subtitle {
      font-size: var(--font-size-base);
    }
    
    .featured-posts__grid {
      grid-template-columns: 1fr;
    }
    
    .blog-posts__grid {
      grid-template-columns: 1fr;
    }
    
    .pagination {
      flex-direction: column;
      gap: var(--space-3);
    }
  }

  .blog-archive {
    margin-top: var(--space-8);
    padding-top: var(--space-6);
    border-top: 1px solid var(--border-light);
  }

  .blog-archive__title {
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-semibold);
    color: var(--text-secondary);
    margin-bottom: var(--space-4);
  }

  .blog-archive__list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: var(--space-2) var(--space-6);
  }

  .blog-archive__link {
    color: var(--text-secondary);
    text-decoration: none;
    font-size: var(--font-size-sm);
    line-height: 1.5;
    border-bottom: 1px solid transparent;
    transition: var(--transition-colors);
  }

  .blog-archive__link:hover {
    color: var(--brand-primary);
    border-bottom-color: var(--brand-primary);
  }
</style>
